import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Phone, Mail, MapPin, Calendar, User, Plus, X,
  CheckCircle2, XCircle, PhoneOff, FileText, Trash2, Save, Edit3, Globe, UserCheck,
  MessageSquare, Clock, Flame, Send, StickyNote, ShieldCheck, ShieldOff, Zap, PhoneCall, Gift, ClipboardList, Camera,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { leadsAPI, carrierAPI, contractTypeAPI, clientsAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, getErrorMessage, formatPhone, formatPhoneInput } from '@/lib/utils';
import LeadUnifiedTimeline from '@/components/leads/LeadUnifiedTimeline';
import LeadFollowUpCard from '@/components/leads/LeadFollowUpCard';
import LeadAppointmentsCard from '@/components/leads/LeadAppointmentsCard';
import LeadDuplicateAlert from '@/components/leads/LeadDuplicateAlert';
import LeadAiSummaryCard from '@/components/leads/LeadAiSummaryCard';
import NeedsAnalysisDrawer from '@/components/leads/NeedsAnalysisDrawer';
import NeedsAnalysisCard from '@/components/leads/NeedsAnalysisCard';
import LeadScorePill from '@/components/leads/LeadScorePill';
import LeadChatTranscriptCard from '@/components/leads/LeadChatTranscriptCard';
import LeadSourcePicker, { labelForSource } from '@/components/leads/LeadSourcePicker';
import EnrollRaffleDialog from '@/components/raffles/EnrollRaffleDialog';import ContactSmsCard from '@/components/sms/ContactSmsCard';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { DocumentsSection } from '@/components/common/DocumentsSection';
import { flashAutofilled } from '@/lib/flashAutofilled';

const STATUS_META = {
  new: { label: 'New', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  attempting_contact: { label: 'Attempting Contact', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  quoted: { label: 'Quoted', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  appointment: { label: 'Appointment', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  closed_won: { label: 'Closed Won', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  not_interested: { label: 'Not Interested', color: 'bg-rose-100 text-rose-700 border-rose-300' },
};

const REASONS = [
  { value: 'price', label: 'Price' },
  { value: 'timing', label: 'Timing' },
  { value: 'competition', label: 'Competition' },
  { value: 'not_qualified', label: 'Not qualified' },
  { value: 'no_response', label: 'No response' },
  { value: 'other', label: 'Other' },
];

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [searchParams] = useSearchParams();
  const smsCardRef = useRef(null);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showClose, setShowClose] = useState(false);
  const [showNI, setShowNI] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [followUps, setFollowUps] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showNeedsAnalysis, setShowNeedsAnalysis] = useState(false);
  const [naRefresh, setNaRefresh] = useState(0);
  const [showNote, setShowNote] = useState(false);
  const [showDisp, setShowDisp] = useState(false);
  const [showRaffle, setShowRaffle] = useState(false);
  const [unreadSms, setUnreadSms] = useState(0);

  const loadFollowUps = useCallback(async () => {
    try {
      const r = await leadsAPI.listFollowUps(id);
      setFollowUps(r.data?.items || r.data?.follow_ups || (Array.isArray(r.data) ? r.data : []));
    } catch { /* noop */ }
  }, [id]);

  const load = useCallback(async () => {
    try {
      const res = await leadsAPI.get(id);
      setLead(res.data);
      setForm(res.data);
      setUnreadSms(Number(res.data?.unread_sms) || 0);
      loadFollowUps();
    } catch { toast.error('Failed to load lead'); }
    finally { setLoading(false); }
  }, [id, loadFollowUps]);

  useEffect(() => { load(); }, [load]);

  // Open the Messages tab and clear the unread SMS counter.
  const openMessages = useCallback(() => {
    setActiveTab('messages');
    if (unreadSms > 0) {
      setUnreadSms(0);
      leadsAPI.markSmsRead(id).catch(() => {});
    }
  }, [id, unreadSms]);

  // Phase 27.38.4 — When arriving from a "New Lead" notification with
  // `?tab=sms`, scroll the SMS drawer into view so the AI welcome
  // suggestions are visible without a manual scroll.
  useEffect(() => {
    if (!lead) return;
    if (searchParams.get('tab') === 'sms') openMessages();
  }, [lead, searchParams, openMessages]);

  const changeStatus = async (status, notes) => {
    try {
      await leadsAPI.changeStatus(id, status, notes);
      toast.success(`Marked as ${STATUS_META[status]?.label || status}`);
      load();
    } catch { toast.error('Failed to update status'); }
  };

  const [converting, setConverting] = useState(false);
  const convertToClient = async () => {
    if (!window.confirm('Convert this Won lead into a Client? The lead will be archived (still counted in reports) and its full activity moved to the new client.')) return;
    setConverting(true);
    try {
      const r = await clientsAPI.convertFromLead(id);
      toast.success('Lead converted to client');
      navigate(`/clients/${r.data.client_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to convert to client');
    } finally { setConverting(false); }
  };

  const saveEdit = async () => {
    try {
      const patch = {};
      ['first_name','last_name','gender','email','phone','address','state','county','product_of_interest','notes','date_of_birth','source','event_request_id','retail_assignment_id','preferred_language','medicare_id','current_plan_carrier','current_plan_name','coverage_need','best_time_to_contact','preferred_contact_method','renewal_date','sales_notes']
        .forEach((k) => { if (form[k] !== lead?.[k]) patch[k] = form[k] ?? ''; });
      if (Object.keys(patch).length === 0) { setEditing(false); return; }
      await leadsAPI.update(id, patch);
      toast.success('Lead updated');
      setEditing(false);
      load();
    } catch { toast.error('Update failed'); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await leadsAPI.delete(id);
      toast.success('Lead deleted');
      navigate('/leads');
    } catch { toast.error('Delete failed'); }
  };

  // Phase 27.43 — Soft-dismiss the duplicate alert once an admin has
  // reviewed the matches. We clear the flag on the lead doc but leave
  // `possible_duplicate_of` intact so the audit trail survives.
  const dismissDuplicateAlert = async () => {
    try {
      await leadsAPI.dismissDuplicateAlert(id);
      toast.success('Duplicate alert dismissed');
      setLead((p) => ({ ...p, has_duplicate_alert: false }));
    } catch {
      toast.error('Failed to dismiss alert');
    }
  };

  const logDisposition = async (disposition, note) => {
    try {
      await leadsAPI.callDisposition(id, disposition, note);
      toast.success('Call logged');
      setShowDisp(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to log call'); }
  };

  const addQuickNote = async (text) => {
    if (!text?.trim()) return;
    try {
      await leadsAPI.addNote(id, text.trim());
      toast.success('Note added');
      setShowNote(false);
      load();
    } catch { toast.error('Failed to add note'); }
  };

  if (loading) return <div className="page-content flex justify-center py-24"><Loader2 className="animate-spin text-slate-400" size={24} /></div>;
  if (!lead) return (
    <div className="page-content" data-testid="lead-unavailable">
      <Card className="max-w-lg mx-auto mt-12 border-slate-200">
        <CardContent className="text-center py-12">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <User className="text-slate-400" size={22} />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">Related record unavailable</h2>
          <p className="text-sm text-slate-500 mt-1">
            This lead is no longer available. It may have been deleted or merged.
            Any historical follow-ups are preserved on your calendar and dashboard.
          </p>
          <Button className="mt-6" variant="outline" onClick={() => navigate('/leads')} data-testid="lead-unavailable-back">
            <ArrowLeft size={14} className="mr-1.5" /> Back to Leads
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const meta = STATUS_META[lead.status] || STATUS_META.new;
  const isClosed = lead.status === 'closed_won' || lead.status === 'not_interested';

  // Next pending follow-up + its urgency.
  const pendingFU = (followUps || [])
    .filter((f) => !f.completed_at && f.status !== 'completed' && f.status !== 'cancelled' && f.scheduled_at)
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const nextFU = pendingFU[0] || null;
  let fuUrgency = null;
  if (nextFU) {
    const diffH = (new Date(nextFU.scheduled_at) - new Date()) / 36e5;
    if (diffH < 0) fuUrgency = 'overdue';
    else if (diffH <= 24) fuUrgency = 'today';
    else if (diffH <= 72) fuUrgency = 'soon';
  }

  // Next Best Action — prioritization, not decoration.
  const nba = (() => {
    if (lead.do_not_contact) return { label: 'Do not contact', tone: 'muted', action: null };
    if (lead.status === 'closed_won') return lead.converted_client_id
      ? { label: 'No action required', tone: 'muted', action: null }
      : { label: 'Convert to client', tone: 'teal', action: convertToClient };
    if (lead.status === 'not_interested') return { label: 'No action required', tone: 'muted', action: null };
    if (fuUrgency === 'overdue') return { label: 'Follow up now (overdue)', tone: 'rose', action: () => setActiveTab('followups') };
    if (fuUrgency === 'today') return { label: 'Follow up today', tone: 'amber', action: () => setActiveTab('followups') };
    if (!lead.last_contact_at) return lead.phone
      ? { label: 'Call now', tone: 'emerald', action: () => window.krSoftphoneDial?.(lead.phone, { contact_type: 'lead', contact_id: lead.lead_id }) }
      : { label: 'Send SMS', tone: 'emerald', action: openMessages };
    if (lead.status === 'new' || lead.status === 'attempting_contact') return { label: 'Call now', tone: 'emerald', action: () => lead.phone ? window.krSoftphoneDial?.(lead.phone, { contact_type: 'lead', contact_id: lead.lead_id }) : openMessages() };
    if (lead.status === 'contacted') return { label: 'Send quote', tone: 'indigo', action: () => changeStatus('quoted') };
    if (lead.status === 'quoted') return { label: 'Schedule appointment', tone: 'purple', action: () => setActiveTab('appointments') };
    return { label: 'Follow up today', tone: 'amber', action: () => setActiveTab('followups') };
  })();
  const NBA_TONE = {
    emerald: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    amber: 'bg-amber-500 hover:bg-amber-600 text-white',
    rose: 'bg-rose-600 hover:bg-rose-700 text-white',
    indigo: 'bg-indigo-600 hover:bg-indigo-700 text-white',
    purple: 'bg-purple-600 hover:bg-purple-700 text-white',
    teal: 'bg-teal-600 hover:bg-teal-700 text-white',
    muted: 'bg-slate-200 text-slate-500 cursor-default',
  };
  const daysSince = Math.max(0, Math.floor((Date.now() - new Date(lead.created_at)) / 864e5));
  const callLead = () => lead.phone
    ? window.krSoftphoneDial?.(lead.phone, { contact_type: 'lead', contact_id: lead.lead_id })
    : toast.error('No phone on file');

  return (
    <div className="page-content space-y-4" data-testid="lead-detail-page">
      {/* Back + edit/delete */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/leads')}>
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <div className="flex gap-2">
          {lead.status === 'closed_won' && !lead.converted_client_id && (
            <Button size="sm" onClick={convertToClient} disabled={converting} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="convert-to-client-btn">
              {converting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <UserCheck size={14} className="mr-1" />} Convert to Client
            </Button>
          )}
          {lead.converted_client_id && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/clients/${lead.converted_client_id}`)} data-testid="view-client-btn">
              <UserCheck size={14} className="mr-1" /> View Client
            </Button>
          )}
          {!editing ? (
            <Button variant="outline" size="sm" onClick={() => setShowNeedsAnalysis(true)} data-testid="start-needs-analysis-btn">
              <ClipboardList size={14} className="mr-1" /> Needs Analysis
            </Button>
          ) : null}
          {!editing ? (
            <Button variant="outline" size="sm" onClick={() => { setEditing(true); setActiveTab('overview'); }} data-testid="edit-lead-btn">
              <Edit3 size={14} className="mr-1" /> Edit
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => { setEditing(false); setForm(lead); }}><X size={14} className="mr-1" /> Cancel</Button>
              <Button size="sm" onClick={saveEdit} data-testid="save-lead-btn"><Save size={14} className="mr-1" /> Save</Button>
            </>
          )}
          {isKR && !editing && (
            <Button variant="outline" size="sm" onClick={remove}><Trash2 size={14} className="mr-1 text-rose-500" /> Delete</Button>
          )}
        </div>
      </div>

      <LeadDuplicateAlert lead={lead} isKR={isKR} onDismiss={dismissDuplicateAlert} />

      {/* ── SALES HEADER ── */}
      <Card className="border-slate-200" data-testid="lead-sales-header">
        <CardContent className="pt-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-heading font-semibold text-slate-900">{lead.first_name} {lead.last_name || ''}</h1>
                <Badge className={`${meta.color} border font-normal`}>{meta.label}</Badge>
                <LeadScorePill score={lead.ai_score} size="md" />
                {lead.do_not_contact && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold px-2 py-0.5 uppercase"><ShieldOff size={10} /> Do not contact</span>
                )}
                {lead.preferred_language && (
                  <span className={`inline-flex items-center rounded-full border text-[10px] font-semibold px-2 py-0.5 uppercase tracking-wide ${lead.preferred_language === 'es' ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-sky-50 border-sky-200 text-sky-700'}`} data-testid="lead-detail-language-chip">
                    {lead.preferred_language === 'es' ? 'Español' : 'English'}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-5 gap-y-2 mt-3">
                <HeaderStat label="Assigned" value={lead.assigned_agent_name || 'Unassigned'} />
                <HeaderStat label="Source" value={labelForSource(lead.source) || lead.source || '—'} />
                <HeaderStat label="Product" value={lead.product_of_interest || '—'} />
                <HeaderStat label="Last contact" value={lead.last_contact_at ? new Date(lead.last_contact_at).toLocaleDateString() : 'Never'} />
                <HeaderStat label="Next follow-up" value={nextFU ? new Date(nextFU.scheduled_at).toLocaleDateString() : '—'}
                  tone={fuUrgency === 'overdue' ? 'rose' : (fuUrgency === 'today' ? 'amber' : null)} />
                <HeaderStat label="Attempts" value={`${lead.contact_attempts ?? 0} · ${daysSince}d old`} />
              </div>
            </div>
            {/* Next Best Action */}
            <div className="w-full sm:w-auto shrink-0 rounded-xl border border-slate-200 bg-slate-50 p-2.5 text-center sm:min-w-[190px]" data-testid="lead-next-best-action">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-center gap-1"><Zap size={11} /> Next Best Action</p>
              <Button size="sm" className={`mt-2 w-full ${NBA_TONE[nba.tone]}`} onClick={nba.action || undefined} disabled={!nba.action} data-testid="nba-button">
                {nba.label}
              </Button>
            </div>
          </div>

          {/* Follow-up alert */}
          {!isClosed && fuUrgency && (
            <div className={`mt-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${fuUrgency === 'overdue' ? 'bg-rose-50 text-rose-800 border border-rose-200' : (fuUrgency === 'today' ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-slate-50 text-slate-700 border border-slate-200')}`} data-testid="followup-alert">
              <Clock size={14} />
              <span className="font-medium">
                {fuUrgency === 'overdue' ? 'Overdue follow-up' : (fuUrgency === 'today' ? 'Follow-up due today' : 'Follow-up due soon')}
              </span>
              <span className="text-xs">· {new Date(nextFU.scheduled_at).toLocaleString()}{nextFU.note ? ` — ${nextFU.note}` : ''}</span>
              <Button size="sm" variant="outline" className="ml-auto h-7" onClick={() => setActiveTab('followups')}>Open</Button>
            </div>
          )}

          {/* ── ACTION BAR (communication) ── */}
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="lead-action-bar">
            <Button size="sm" variant="outline" onClick={callLead} data-testid="action-call"><Phone size={14} className="mr-1 text-emerald-600" /> Call</Button>
            <Button size="sm" variant="outline" onClick={() => setShowDisp(true)} data-testid="action-log-call"><PhoneCall size={14} className="mr-1 text-emerald-700" /> Log Call</Button>
            <Button size="sm" variant="outline" onClick={openMessages} data-testid="action-text">
              <MessageSquare size={14} className="mr-1 text-sky-600" /> Text
              {unreadSms > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold" data-testid="action-text-unread">{unreadSms}</span>}
            </Button>
            <Button size="sm" variant="outline" onClick={() => lead.email ? window.open(`mailto:${lead.email}`) : toast.error('No email on file')} data-testid="action-email"><Mail size={14} className="mr-1 text-indigo-600" /> Email</Button>
            <Button size="sm" variant="outline" onClick={() => setActiveTab('appointments')} data-testid="action-schedule"><Calendar size={14} className="mr-1 text-purple-600" /> Schedule</Button>
            <Button size="sm" variant="outline" onClick={() => setActiveTab('followups')} data-testid="action-followup"><Clock size={14} className="mr-1 text-amber-600" /> Add Follow-up</Button>
            <Button size="sm" variant="outline" onClick={() => setShowNote(true)} data-testid="action-note"><StickyNote size={14} className="mr-1 text-slate-600" /> Add Note</Button>
            <Button size="sm" variant="outline" onClick={() => setShowRaffle(true)} data-testid="action-raffle"><Gift size={14} className="mr-1 text-amber-600" /> Enter Raffle</Button>
          </div>

          {/* Pipeline status (separate from comms) — horizontal scroll on mobile */}
          {!isClosed && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none]" data-testid="lead-pipeline-bar">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-1 shrink-0">Pipeline</span>
              <Button variant="ghost" size="sm" className="h-7 text-amber-700 shrink-0" onClick={() => changeStatus('attempting_contact')} data-testid="qa-attempting">Attempting</Button>
              <Button variant="ghost" size="sm" className="h-7 text-blue-700 shrink-0" onClick={() => changeStatus('contacted')} data-testid="qa-contacted">Contacted</Button>
              <Button variant="ghost" size="sm" className="h-7 text-indigo-700 shrink-0" onClick={() => changeStatus('quoted')} data-testid="qa-quoted">Quoted</Button>
              <Button variant="ghost" size="sm" className="h-7 text-purple-700 shrink-0" onClick={() => changeStatus('appointment')} data-testid="qa-appointment">Appointment</Button>
              <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 shrink-0" onClick={() => setShowClose(true)} data-testid="qa-close-won"><CheckCircle2 size={13} className="mr-1" /> Close Won</Button>
              <Button variant="ghost" size="sm" className="h-7 text-rose-700 shrink-0" onClick={() => setShowNI(true)} data-testid="qa-not-interested">Not Interested</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <LeadAiSummaryCard leadId={id} />

      {lead.is_merged && lead.merged_into && (
        <div className="rounded-xl border border-slate-300 bg-slate-100 p-3 flex items-center justify-between gap-3" data-testid="lead-merged-notice">
          <div className="text-sm text-slate-700"><span className="font-semibold">This lead was merged</span> into another record.</div>
          <Button size="sm" variant="outline" onClick={() => navigate(`/leads/${lead.merged_into}`)} data-testid="open-merged-into">Open surviving lead →</Button>
        </div>
      )}

      {/* ── TABS ── */}
      <Tabs value={activeTab} onValueChange={(v) => (v === 'messages' ? openMessages() : setActiveTab(v))} className="w-full">
        <TabsList className="flex-wrap h-auto" data-testid="lead-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups">Follow-ups{pendingFU.length ? ` (${pendingFU.length})` : ''}</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages" className="relative">
            Messages
            {unreadSms > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold" data-testid="tab-messages-unread">{unreadSms}</span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle className="text-base">Contact & Sales Needs</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-3 text-sm">
                <Field label="First name" edit={editing} value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} raw={lead.first_name} />
                <Field label="Last name" edit={editing} value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} raw={lead.last_name} />
                <Field label="Email" icon={Mail} edit={editing} value={form.email} onChange={(v) => setForm({ ...form, email: v })} raw={lead.email} />
                <Field label="Phone" icon={Phone} edit={editing} value={formatPhoneInput(form.phone)} onChange={(v) => setForm({ ...form, phone: v })} raw={formatPhone(lead.phone)} />
                <Field label="State" edit={editing} value={form.state} onChange={(v) => setForm({ ...form, state: v })} raw={lead.state} />
                <Field label="County" edit={editing} value={form.county} onChange={(v) => setForm({ ...form, county: v })} raw={lead.county} />
                <Field label="Product of interest" edit={editing} value={form.product_of_interest} onChange={(v) => setForm({ ...form, product_of_interest: v })} raw={lead.product_of_interest} span2 />
                <Field label="Coverage need" edit={editing} value={form.coverage_need} onChange={(v) => setForm({ ...form, coverage_need: v })} raw={lead.coverage_need} />
                <Field label="Current carrier" edit={editing} value={form.current_plan_carrier} onChange={(v) => setForm({ ...form, current_plan_carrier: v })} raw={lead.current_plan_carrier} />
                <Field label="Current plan" edit={editing} value={form.current_plan_name} onChange={(v) => setForm({ ...form, current_plan_name: v })} raw={lead.current_plan_name} />
                <Field label="Renewal / effective date" type="date" edit={editing} value={form.renewal_date?.slice(0,10)} onChange={(v) => setForm({ ...form, renewal_date: v })} raw={lead.renewal_date ? String(lead.renewal_date).slice(0,10) : null} />
                <Field label="Best time to contact" edit={editing} value={form.best_time_to_contact} onChange={(v) => setForm({ ...form, best_time_to_contact: v })} raw={lead.best_time_to_contact} />
                <Field label="Preferred method" edit={editing} value={form.preferred_contact_method} onChange={(v) => setForm({ ...form, preferred_contact_method: v })} raw={lead.preferred_contact_method} />
                <Field label="Medicare ID" edit={editing} value={form.medicare_id} onChange={(v) => setForm({ ...form, medicare_id: v })} raw={lead.medicare_id} />
                <FieldLang edit={editing} value={form.preferred_language} onChange={(v) => setForm({ ...form, preferred_language: v })} raw={lead.preferred_language === 'es' ? 'Español' : 'English'} />
                <div className="col-span-2">
                  <Label className="text-xs">Important sales notes</Label>
                  {editing ? (
                    <Textarea rows={2} value={form.sales_notes || ''} onChange={(e) => setForm({ ...form, sales_notes: e.target.value })} />
                  ) : (
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{lead.sales_notes || <span className="text-slate-400 italic">—</span>}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Notes</Label>
                  {editing ? (
                    <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  ) : (
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{lead.notes || <span className="text-slate-400 italic">—</span>}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              {/* Snapshot — consent + contact attempts combined */}
              <Card data-testid="lead-snapshot-card">
                <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-1"><ShieldCheck size={14} className="text-emerald-600" /> Snapshot</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1.5">
                    <ConsentChip label="SMS" ok={!lead.do_not_contact && !!lead.phone} note={!lead.phone ? 'no phone' : (lead.do_not_contact ? 'blocked' : 'phone on file')} />
                    <ConsentChip label="Call" ok={!lead.do_not_contact && !!lead.phone} note={!lead.phone ? 'no phone' : (lead.do_not_contact ? 'blocked' : 'phone on file')} />
                    <ConsentChip label="Email" ok={!lead.do_not_contact && !!lead.email} note={!lead.email ? 'no email' : (lead.do_not_contact ? 'blocked' : 'email on file')} />
                  </div>
                  <div className="pt-2 border-t border-slate-100 space-y-2 text-sm">
                    <Row label="Attempts" value={lead.contact_attempts ?? 0} />
                    {lead.last_contact_at && <Row label="Last contact" value={new Date(lead.last_contact_at).toLocaleString()} />}
                    {lead.last_contact_method && <Row label="Last method" value={<span className="capitalize">{lead.last_contact_method}</span>} />}
                    {lead.last_call_outcome && <Row label="Last outcome" value={<span className="capitalize">{String(lead.last_call_outcome).replace(/_/g, ' ')}</span>} />}
                    <Row label="Days since created" value={daysSince} />
                    {lead.source && <Row label="Source" value={labelForSource(lead.source) || lead.source} />}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          <NeedsAnalysisCard leadId={id} refreshKey={naRefresh} onEdit={() => setShowNeedsAnalysis(true)} />
          <LeadChatTranscriptCard lead={lead} />
          <LeadEnrollmentCard lead={lead} onSaved={load} />
          <LeadDocumentsCard lead={lead} onSaved={load} />
          {lead.status === 'closed_won' && lead.sales?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Products Sold ({lead.sales.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {lead.sales.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 border rounded p-2">
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 font-normal border">{s.product_line || 'Other'}</Badge>
                    <div className="flex-1"><p className="font-medium text-slate-900">{s.contract_type_name || 'Product'}</p><p className="text-xs text-slate-500">{s.carrier_name || 'Unknown carrier'}</p></div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {lead.status === 'not_interested' && (
            <Card className="border-rose-200 bg-rose-50"><CardContent className="pt-4">
              <p className="text-sm text-rose-900"><span className="font-medium">Not Interested — </span><span className="capitalize">{(lead.not_interested_reason || '').replace('_', ' ')}</span>{lead.not_interested_details && <> — <span className="italic">{lead.not_interested_details}</span></>}</p>
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <LeadUnifiedTimeline leadId={id} onChange={load} />
        </TabsContent>

        <TabsContent value="followups" className="mt-4">
          {isClosed ? <EmptyTab text="Follow-ups are disabled for closed leads." /> : <LeadFollowUpCard leadId={id} onChange={load} />}
        </TabsContent>

        <TabsContent value="appointments" className="mt-4">
          {isClosed ? <EmptyTab text="Appointments are disabled for closed leads." /> : <LeadAppointmentsCard lead={lead} onChange={load} />}
        </TabsContent>

        <TabsContent value="messages" className="mt-4">
          <div ref={smsCardRef}>
            <ContactSmsCard contactType="lead" contactId={lead.lead_id} phone={lead.phone} />
          </div>
        </TabsContent>
      </Tabs>

      <CloseWonDialog open={showClose} onOpenChange={setShowClose} leadId={id} onDone={() => { setLead((l) => (l ? { ...l, status: 'closed_won' } : l)); load(); }} />
      <NotInterestedDialog open={showNI} onOpenChange={setShowNI} leadId={id} onDone={load} />
      <QuickNoteDialog open={showNote} onOpenChange={setShowNote} onSave={addQuickNote} />
      <DispositionDialog open={showDisp} onOpenChange={setShowDisp} onSave={logDisposition} />
      <EnrollRaffleDialog
        open={showRaffle}
        onOpenChange={setShowRaffle}
        leadId={id}
        leadName={lead ? `${lead.first_name} ${lead.last_name || ''}`.trim() : ''}
        onDone={load}
      />
      <NeedsAnalysisDrawer
        open={showNeedsAnalysis}
        onOpenChange={setShowNeedsAnalysis}
        leadId={id}
        onSaved={() => setNaRefresh((x) => x + 1)}
      />
    </div>
  );
}

function HeaderStat({ label, value, tone }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className={`text-sm truncate ${tone === 'rose' ? 'text-rose-700 font-semibold' : (tone === 'amber' ? 'text-amber-700 font-semibold' : 'text-slate-800')}`} title={typeof value === 'string' ? value : undefined}>{value}</p>
    </div>
  );
}

function ConsentChip({ label, ok, note }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${ok ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
        {ok ? <ShieldCheck size={11} /> : <ShieldOff size={11} />} {note}
      </span>
    </div>
  );
}

function EmptyTab({ text }) {
  return <Card><CardContent className="py-8 text-center text-sm text-slate-500">{text}</CardContent></Card>;
}

const DISPOSITIONS = [
  { value: 'no_answer', label: 'No Answer' },
  { value: 'left_voicemail', label: 'Left Voicemail' },
  { value: 'interested', label: 'Interested' },
  { value: 'call_back', label: 'Call Back' },
  { value: 'appointment_set', label: 'Appointment Set' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'wrong_number', label: 'Wrong Number' },
  { value: 'do_not_contact', label: 'Do Not Contact' },
];

function DispositionDialog({ open, onOpenChange, onSave }) {
  const [disp, setDisp] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => { if (open) { setDisp(''); setNote(''); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="disposition-dialog">
        <DialogHeader><DialogTitle>Log call outcome</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs">Disposition</Label>
            <Select value={disp} onValueChange={setDisp}>
              <SelectTrigger data-testid="disposition-select"><SelectValue placeholder="Select outcome…" /></SelectTrigger>
              <SelectContent>
                {DISPOSITIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened on the call…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(disp, note)} disabled={!disp} data-testid="disposition-save">Log Call</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuickNoteDialog({ open, onOpenChange, onSave }) {
  const [text, setText] = useState('');
  useEffect(() => { if (open) setText(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-note-dialog">
        <DialogHeader><DialogTitle>Add note</DialogTitle></DialogHeader>
        <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a quick note…" data-testid="quick-note-text" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(text)} disabled={!text.trim()} data-testid="quick-note-save">Save Note</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-slate-100 pb-1.5 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}

function Field({ label, icon: Icon, edit, value, onChange, raw, span2, type = 'text', testId }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <Label className="text-xs">{label}</Label>
      {edit ? (
        <Input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      ) : (
        <p className="text-sm text-slate-700 flex items-center gap-1">
          {Icon && <Icon size={12} className="text-slate-400" />}
          {raw || <span className="text-slate-400 italic">—</span>}
        </p>
      )}
    </div>
  );
}

function FieldGender({ edit, value, onChange, raw }) {
  return (
    <div>
      <Label className="text-xs">Gender</Label>
      {edit ? (
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="F">F</SelectItem>
            <SelectItem value="M">M</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <p className="text-sm text-slate-700">{raw || <span className="text-slate-400 italic">—</span>}</p>
      )}
    </div>
  );
}

function FieldLang({ edit, value, onChange, raw }) {
  return (
    <div>
      <Label className="text-xs">Preferred language</Label>
      {edit ? (
        <Select value={value || 'en'} onValueChange={onChange}>
          <SelectTrigger data-testid="edit-lead-language"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="es">Español</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <p className="text-sm text-slate-700">{raw || <span className="text-slate-400 italic">—</span>}</p>
      )}
    </div>
  );
}

function CloseWonDialog({ open, onOpenChange, leadId, onDone }) {
  const PRODUCT_LINES = ['ACA', 'Life', 'Medicare', 'Ancillary', 'Annuity', 'Final Expense', 'Other'];
  const [carriers, setCarriers] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [sales, setSales] = useState([{ product_line: '', carrier_id: '' }]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([carrierAPI.list(), contractTypeAPI.list()]).then(([c, ct]) => {
      setCarriers(c.data || []);
      setContractTypes(ct.data || []);
    }).catch(() => {});
    setSales([{ product_line: '', carrier_id: '' }]);
    setNotes('');
  }, [open]);

  const addRow = () => setSales([...sales, { product_line: '', carrier_id: '' }]);
  const removeRow = (i) => setSales(sales.filter((_, idx) => idx !== i));
  const setRow = (i, patch) => setSales(sales.map((s, idx) => idx === i ? { ...s, ...patch } : s));

  const submit = async () => {
    const valid = sales.filter((s) => s.product_line && s.carrier_id);
    if (valid.length === 0) { toast.error('Pick a product and carrier'); return; }
    setSaving(true);
    try {
      const payload = valid.map((s) => {
        const c = carriers.find((x) => (x.carrier_id || x.id) === s.carrier_id);
        // Resolve a matching contract type for this carrier + product line, if any.
        const ct = contractTypes.find(
          (x) => x.carrier_id === s.carrier_id && (x.product_line || '') === s.product_line
        );
        return {
          carrier_id: s.carrier_id,
          carrier_name: c?.name || c?.carrier_name,
          contract_type_id: ct?.contract_type_id || null,
          contract_type_name: ct?.contract_type_name || null,
          product_line: s.product_line,
        };
      });
      await leadsAPI.closeWon(leadId, payload, notes);
      toast.success('Lead closed as won!');
      onOpenChange(false);
      onDone();
    } catch { toast.error('Close failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Close Won — record products sold</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {sales.map((s, i) => {
            // Carriers that offer this product line (fall back to all carriers
            // when no contract types are tagged with the product line yet).
            const matching = contractTypes.filter((ct) => (ct.product_line || '') === s.product_line);
            const carrierIds = new Set(matching.map((ct) => ct.carrier_id));
            const carrierOpts = (s.product_line && carrierIds.size > 0)
              ? carriers.filter((c) => carrierIds.has(c.carrier_id || c.id))
              : carriers;
            return (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div>
                  <Label className="text-xs">Product *</Label>
                  <Select value={s.product_line} onValueChange={(v) => setRow(i, { product_line: v, carrier_id: '' })}>
                    <SelectTrigger data-testid={`sale-product-${i}`}><SelectValue placeholder="Pick product" /></SelectTrigger>
                    <SelectContent>
                      {PRODUCT_LINES.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Carrier *</Label>
                  <Select value={s.carrier_id} onValueChange={(v) => setRow(i, { carrier_id: v })} disabled={!s.product_line}>
                    <SelectTrigger data-testid={`sale-carrier-${i}`}><SelectValue placeholder="Pick carrier" /></SelectTrigger>
                    <SelectContent>
                      {carrierOpts.map((c, ci) => (
                        <SelectItem key={c.carrier_id || c.id || `carrier-${ci}`} value={c.carrier_id || c.id || ''}>
                          {c.name || c.carrier_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeRow(i)} disabled={sales.length === 1}>
                  <X size={14} />
                </Button>
              </div>
            );
          })}
          <Button variant="outline" size="sm" onClick={addRow} data-testid="add-sale-row">
            <Plus size={14} className="mr-1" /> Add product
          </Button>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={saving} data-testid="close-won-submit">
            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
            Mark as Closed Won
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotInterestedDialog({ open, onOpenChange, leadId, onDone }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setReason(''); setDetails(''); } }, [open]);
  const submit = async () => {
    if (!reason) { toast.error('Pick a reason'); return; }
    setSaving(true);
    try {
      await leadsAPI.notInterested(leadId, reason, details);
      toast.success('Marked as Not Interested');
      onOpenChange(false);
      onDone();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Mark as Not Interested</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="ni-reason-select"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Details</Label>
            <Textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Optional additional context" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-rose-600 hover:bg-rose-700" onClick={submit} disabled={saving} data-testid="ni-submit">
            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Sale / Enrollment details ──────────────────────────────────────
// Captures the SSN (encrypted) and dependents on the LEAD so they
// transfer to the client on conversion. SSN is masked by default with
// a Reveal action (audited server-side).
function LeadEnrollmentCard({ lead, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ssn, setSsn] = useState('');
  const [revealed, setRevealed] = useState(null);
  const [deps, setDeps] = useState(lead.dependents || []);

  useEffect(() => { setDeps(lead.dependents || []); setSsn(''); setRevealed(null); }, [lead.lead_id, lead.updated_at]);

  const reveal = async () => {
    try {
      const r = await leadsAPI.revealSsn(lead.lead_id);
      setRevealed(r.data?.ssn || '—');
    } catch (e) { toast.error(getErrorMessage(e, 'Could not reveal SSN')); }
  };

  const addDep = () => setDeps((d) => [...d, { first_name: '', last_name: '', relationship: 'spouse', date_of_birth: '', ssn: '' }]);
  const setDep = (i, k, v) => setDeps((d) => d.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const rmDep = (i) => setDeps((d) => d.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { dependents: deps.map((d) => ({ ...d })) };
      if (ssn.trim()) payload.ssn = ssn.trim();
      await leadsAPI.update(lead.lead_id, payload);
      toast.success('Enrollment details saved');
      setEditing(false); setSsn('');
      onSaved && onSaved();
    } catch (e) { toast.error(getErrorMessage(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="lead-enrollment-card">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base">Sale / Enrollment details</CardTitle>
        {editing ? (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDeps(lead.dependents || []); setSsn(''); }} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="lead-enrollment-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="lead-enrollment-edit"><Edit3 className="h-4 w-4 mr-1" /> Edit</Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div>
          <Label className="text-xs">Social Security Number (SSN)</Label>
          {editing ? (
            <Input value={ssn} onChange={(e) => setSsn(e.target.value)} placeholder={lead.has_ssn ? 'Enter to replace (leave blank to keep)' : 'XXX-XX-XXXX'} data-testid="lead-ssn-input" />
          ) : (
            <div className="flex items-center gap-3 mt-1">
              <span className="font-mono text-slate-800" data-testid="lead-ssn-display">{revealed || lead.ssn_masked || <span className="text-slate-400 italic">Not on file</span>}</span>
              {lead.has_ssn && !revealed && (
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={reveal} data-testid="lead-ssn-reveal">Reveal</Button>
              )}
            </div>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs">Dependents</Label>
            {editing && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addDep} data-testid="lead-dep-add"><Plus className="h-3 w-3 mr-1" /> Add dependent</Button>}
          </div>
          {deps.length === 0 ? (
            <p className="text-slate-400 italic">No dependents.</p>
          ) : (
            <ul className="space-y-2">
              {deps.map((d, i) => (
                <li key={i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`lead-dep-${i}`}>
                  {editing ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={d.first_name || ''} onChange={(e) => setDep(i, 'first_name', e.target.value)} placeholder="First name" className="h-8 text-sm" />
                      <Input value={d.last_name || ''} onChange={(e) => setDep(i, 'last_name', e.target.value)} placeholder="Last name" className="h-8 text-sm" />
                      <Input value={d.relationship || ''} onChange={(e) => setDep(i, 'relationship', e.target.value)} placeholder="Relationship" className="h-8 text-sm" />
                      <Input type="date" value={(d.date_of_birth || '').slice(0, 10)} onChange={(e) => setDep(i, 'date_of_birth', e.target.value)} className="h-8 text-sm" />
                      <Input value={d.ssn || ''} onChange={(e) => setDep(i, 'ssn', e.target.value)} placeholder={d.has_ssn ? 'SSN on file — enter to replace' : 'SSN (optional)'} className="h-8 text-sm col-span-2" />
                      <div className="col-span-2 flex justify-end">
                        <Button size="sm" variant="ghost" className="h-7 text-rose-600 hover:text-rose-700" onClick={() => rmDep(i)} data-testid={`lead-dep-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-slate-800">{d.first_name} {d.last_name}</div>
                        <div className="text-xs text-slate-500">{d.relationship || '—'}{d.date_of_birth ? ` · ${String(d.date_of_birth).slice(0, 10)}` : ''}{d.ssn_masked ? ` · SSN ${d.ssn_masked}` : ''}</div>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Lead photo + documents ─────────────────────────────────────────
// The kiosk "Capture Lead" photo (and any files the agent uploads)
// live here and transfer to the client on conversion.
function LeadDocumentsCard({ lead, onSaved }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);

  useEffect(() => {
    let revoked = null;
    if (lead.photo_url) {
      leadsAPI.photoBlob(lead.lead_id)
        .then((r) => { const u = URL.createObjectURL(r.data); revoked = u; setUrl(u); })
        .catch(() => setUrl(null));
    } else { setUrl(null); }
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [lead.lead_id, lead.photo_url]);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      await leadsAPI.uploadPhoto(lead.lead_id, f);
      toast.success('Photo updated');
      onSaved && onSaved();
    } catch (err) { toast.error(getErrorMessage(err, 'Photo upload failed')); }
    finally { setBusy(false); if (photoRef.current) photoRef.current.value = ''; }
  };

  const removePhoto = async () => {
    setBusy(true);
    try { await leadsAPI.deletePhoto(lead.lead_id); onSaved && onSaved(); }
    catch (err) { toast.error(getErrorMessage(err, 'Could not remove photo')); }
    finally { setBusy(false); }
  };

  const initials = `${(lead.first_name || '?')[0] || ''}${(lead.last_name || '')[0] || ''}`.toUpperCase();

  return (
    <Card data-testid="lead-documents-card">
      <CardHeader className="py-3"><CardTitle className="text-base">Photo &amp; documents</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 font-semibold shrink-0" data-testid="lead-photo">
            {url ? <img src={url} alt={initials} className="h-full w-full object-cover" data-testid="lead-photo-img" /> : <span>{initials || '?'}</span>}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={photoRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onPhoto} data-testid="lead-photo-input" />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => photoRef.current?.click()} disabled={busy} data-testid="lead-photo-upload">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Camera className="h-4 w-4 mr-1" /> {lead.photo_url ? 'Replace photo' : 'Add photo'}</>}
              </Button>
              {lead.photo_url && <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={removePhoto} disabled={busy} data-testid="lead-photo-remove"><Trash2 className="h-4 w-4" /></Button>}
            </div>
            <p className="text-[11px] text-slate-400">This becomes the client's profile photo on conversion.</p>
          </div>
        </div>
        <DocumentsSection entityId={lead.lead_id} docsApi={leadsAPI} title="Attachments" testPrefix="lead-doc" />
      </CardContent>
    </Card>
  );
}
